import React from "react";
import { Eye, Handshake, Wrench, ArrowRight } from "lucide-react";
import { buildWattzapOSUrl, openEnergyScanPortal } from "../lib/wattzapOS";
import { useT } from "../i18n/LanguageContext";

function Value({ icon: Icon, title, body }) {
  const t = useT();
  return (
    <div className="bg-white rounded-2xl p-7 border border-slate-100">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan/10 to-energy/10 flex items-center justify-center text-[#00D4FF] mb-4">
        <Icon size={22} />
      </div>
      <h3 className="font-heading text-lg text-graphite">{t(title)}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t(body)}</p>
    </div>
  );
}

export default function About() {
  const t = useT();
  return (
    <div>
      <section className="bg-[#071A2D] text-white pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#00D4FF] mb-3">{t("About WattZap")}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl max-w-3xl">{t("Industrial-grade energy intelligence, for Indian SMEs.")}</h1>
          <p className="mt-6 text-white/75 max-w-2xl">
            {t("WattZap was founded to bring the kind of energy intelligence used by large European utilities — real-time monitoring, tariff-aware optimisation, financed clean generation — to Indian factories and rice mills. Without the enterprise price tag, and without asking anyone to spend crores on capex.")}
          </p>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#00D4FF] mb-3">{t("Our story")}</div>
            <h2 className="font-heading text-3xl text-graphite">{t("Built by operators, not consultants.")}</h2>
            <div data-testid="company-story" className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>
                {t("WattZap brings together deep experience from European energy markets — flexibility, EV charging, batteries, heat pumps, VPPs and industrial energy systems — applied to the realities of Indian businesses.")}
              </p>
              <p>
                {t("That experience revealed a major opportunity in India: businesses were losing money every month through inefficient energy usage, tariff mismatch, power factor penalties, and under-optimised solar adoption.")}
              </p>
              <p>
                {t("WattZap started in Hyderabad with one mission — to make energy savings simple, practical, and measurable for real businesses. By combining solar, EMS Lite, tariff intelligence, and operator-led optimisation, WattZap helps customers cut bills without needing to become energy experts themselves.")}
              </p>
              <p className="font-heading text-graphite">
                {t("Because the future of energy is not just generation — it is intelligent operation.")}
              </p>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-100 p-7 bg-mist">
            <div className="text-xs uppercase tracking-widest text-[#FFC857] mb-3">{t("Where we work")}</div>
            <p className="text-sm text-slate-700 leading-relaxed">{t("Headquartered in Hyderabad, Telangana. Serving industrial customers across India, with deep roots in Telangana and Andhra Pradesh — starting with rice mills, expanding to cold storage, agro-processing, textiles and commercial facilities.")}</p>
          </div>
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-widest text-[#00D4FF] mb-3">{t("What we believe")}</div>
            <h2 className="font-heading text-3xl text-graphite">{t("Three values, non-negotiable.")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Value icon={Eye} title="Transparency" body="We show the math. Every claim in a proposal is traceable to a bill line item, a load reading, or an engineering assumption." />
            <Value icon={Handshake} title="Alignment" body="We earn when you save. Our commercial model is built around your per-unit rate — not upfront margin on hardware." />
            <Value icon={Wrench} title="Engineering rigour" body="Utility-grade monitoring, protection and O&M. No shortcuts on safety, no cutting corners on availability." />
          </div>
        </div>
      </section>

      <section className="bg-[#071A2D] text-white py-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="font-heading text-3xl">{t("Ready to see what your bill hides?")}</h3>
          <a href={buildWattzapOSUrl()} onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }} data-testid="about-cta" className="mt-6 inline-flex items-center gap-2 btn-gradient px-6 py-3 rounded-full text-sm">
            {t("Get a free EnergyScan")} <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
